import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { 
  LayoutDashboard, BookOpen, FileText, Sparkles, Wind, Calendar, 
  Users, TrendingUp, UserCheck, UserX, Shield, Palette, ChevronRight,
  MessageCircle, Package, BarChart3, Share2, Megaphone
} from 'lucide-react';

export default function Admin() {
  const { user, token, hasRole } = useAuth();
  const { currentColors } = useTheme();
  const [analytics, setAnalytics] = useState(null);
  const [practitioners, setPractitioners] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = hasRole(['super_admin', 'user_admin', 'content_admin', 'practitioner_admin']);
  const isPractitionerAdmin = hasRole(['super_admin', 'practitioner_admin']);

  useEffect(() => {
    if (isAdmin) {
      loadAnalytics();
    }
    if (isPractitionerAdmin) {
      loadPractitioners();
    }
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPractitioners = async () => {
    try {
      const response = await axios.get(`${API}/admin/users?role=practitioner`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPractitioners(response.data.users || []);
    } catch (error) {
      console.error('Failed to load practitioners:', error);
    }
  };

  if (!isAdmin) {
    return (
      <div className="glass-card p-12 text-center">
        <Shield className="w-16 h-16 text-cosmic-gold/50 mx-auto mb-4" />
        <h1 className="text-2xl font-cinzel text-cosmic-goldLight mb-2">Access Restricted</h1>
        <p className="text-cosmic-starWhite/60 font-cormorant">You don't have permission to access this area.</p>
      </div>
    );
  }

  const quickLinks = [
    { to: '/admin/users', label: 'Manage Users', icon: Users, color: 'from-cosmic-purple to-cosmic-pink', roles: ['super_admin', 'user_admin'] },
    { to: '/admin/content', label: 'Manage Content', icon: FileText, color: 'from-cosmic-celestial to-cosmic-turquoise', roles: ['super_admin', 'content_admin'] },
    { to: '/admin/practitioners', label: 'Practitioners', icon: Calendar, color: 'from-cosmic-gold to-cosmic-lotus', roles: ['super_admin', 'practitioner_admin'] },
    { to: '/admin/theme', label: 'Theme Settings', icon: Palette, color: 'from-chakra-heart to-chakra-throat', roles: ['super_admin', 'content_admin'] },
    { to: '/admin/support', label: 'Support Center', icon: MessageCircle, color: 'from-blue-500 to-indigo-500', roles: ['super_admin', 'user_admin', 'content_admin'] },
    { to: '/admin/products', label: 'Products', icon: Package, color: 'from-green-500 to-teal-500', roles: ['super_admin', 'content_admin'] },
    { to: '/admin/sales', label: 'Sales Dashboard', icon: BarChart3, color: 'from-amber-500 to-orange-500', roles: ['super_admin', 'content_admin'] },
    { to: '/admin/social', label: 'Social Media', icon: Share2, color: 'from-pink-500 to-rose-500', roles: ['super_admin', 'content_admin'] },
    { to: '/admin/marketing', label: 'Marketing Tools', icon: Megaphone, color: 'from-purple-500 to-violet-500', roles: ['super_admin', 'content_admin', 'practitioner'] },
  ].filter(link => hasRole(link.roles));

  return (
    <div className="space-y-8" data-testid="admin-panel">
      <div className="glass-card p-6 relative overflow-hidden">
        <div className="absolute inset-0 sacred-geometry opacity-20"></div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <LayoutDashboard className="w-8 h-8 text-cosmic-gold" />
            <h1 className="text-2xl sm:text-3xl font-cinzel gold-text">Admin Dashboard</h1>
          </div>
          <p className="text-cosmic-starWhite/70 font-cormorant">Manage The Aura Space platform</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {quickLinks.map((link) => {
          const Icon = link.icon;
          return (
            <Link
              key={link.to}
              to={link.to}
              className="glass-card p-6 group hover:scale-[1.02] transition-all"
            >
              <div className={`w-14 h-14 bg-gradient-to-br ${link.color} rounded-xl flex items-center justify-center mb-4 group-hover:shadow-lg transition-all`}>
                <Icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-cinzel text-lg text-cosmic-goldLight group-hover:text-cosmic-gold transition-colors">
                {link.label}
              </h3>
            </Link>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-12 h-12 border-4 border-cosmic-gold/30 border-t-cosmic-gold rounded-full animate-spin"></div>
        </div>
      ) : analytics && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-cosmic-starWhite/60 text-sm font-cormorant">Total Users</span>
                <Users className="w-5 h-5 text-cosmic-purple" />
              </div>
              <p className="text-3xl font-cinzel gold-text">{analytics.total_users}</p>
            </div>
            
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-cosmic-starWhite/60 text-sm font-cormorant">Active</span>
                <UserCheck className="w-5 h-5 text-chakra-heart" />
              </div>
              <p className="text-3xl font-cinzel text-chakra-heart">{analytics.active_users}</p>
            </div>
            
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-cosmic-starWhite/60 text-sm font-cormorant">Blocked</span>
                <UserX className="w-5 h-5 text-red-400" />
              </div>
              <p className="text-3xl font-cinzel text-red-400">{analytics.blocked_users}</p>
            </div>
            
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-cosmic-starWhite/60 text-sm font-cormorant">Bookings</span>
                <TrendingUp className="w-5 h-5 text-cosmic-turquoise" />
              </div>
              <p className="text-3xl font-cinzel text-cosmic-turquoise">{analytics.total_bookings}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="glass-card p-6">
              <h3 className="font-cinzel text-lg text-cosmic-goldLight mb-4">Content Overview</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-cosmic-gold/10">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-cosmic-purple" />
                    <span className="text-cosmic-starWhite/80 font-cormorant">Meditations</span>
                  </div>
                  <span className="text-cosmic-goldLight font-cinzel">{analytics.total_meditations}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-cosmic-gold/10">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-cosmic-celestial" />
                    <span className="text-cosmic-starWhite/80 font-cormorant">Articles</span>
                  </div>
                  <span className="text-cosmic-goldLight font-cinzel">{analytics.total_articles}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-cosmic-turquoise" />
                    <span className="text-cosmic-starWhite/80 font-cormorant">Healing Sessions</span>
                  </div>
                  <span className="text-cosmic-goldLight font-cinzel">{analytics.total_sessions}</span>
                </div>
              </div>
            </div>

            <div className="glass-card p-6">
              <h3 className="font-cinzel text-lg text-cosmic-goldLight mb-4">Role Distribution</h3>
              <div className="space-y-3">
                {analytics.role_counts && Object.entries(analytics.role_counts).map(([role, count]) => (
                  <div key={role} className="flex items-center justify-between py-2 border-b border-cosmic-gold/10 last:border-0">
                    <span className="text-cosmic-starWhite/80 font-cormorant capitalize">{role.replace('_', ' ')}</span>
                    <span className="text-cosmic-goldLight font-cinzel">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {analytics.recent_users?.length > 0 && (
            <div className="glass-card p-6">
              <h3 className="font-cinzel text-lg text-cosmic-goldLight mb-4">Recent Users</h3>
              <div className="space-y-3">
                {analytics.recent_users.map((u) => (
                  <div key={u.id} className="flex items-center justify-between py-2 border-b border-cosmic-gold/10 last:border-0">
                    <div>
                      <p className="text-cosmic-starWhite font-cormorant">{u.name}</p>
                      <p className="text-cosmic-starWhite/50 text-sm">{u.email}</p>
                    </div>
                    <span className="text-cosmic-starWhite/50 text-sm">
                      {new Date(u.created_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* My Practitioners Widget - For practitioner_admin role */}
      {isPractitionerAdmin && practitioners.length > 0 && (
        <div 
          className="glass-card p-6"
          style={{ borderColor: `${currentColors.accent}30` }}
          data-testid="my-practitioners-widget"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-cinzel text-lg flex items-center gap-2" style={{ color: currentColors.primary }}>
              <UserCheck className="w-5 h-5" style={{ color: currentColors.accent }} />
              My Practitioners
            </h3>
            <Link
              to="/admin/practitioners"
              className="text-sm flex items-center gap-1 hover:opacity-80 transition-all"
              style={{ color: currentColors.accent }}
            >
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {practitioners.slice(0, 6).map((practitioner) => (
              <div 
                key={practitioner.id}
                className="p-4 rounded-lg transition-all hover:opacity-90"
                style={{ 
                  backgroundColor: `${currentColors.secondary}10`,
                  border: `1px solid ${currentColors.border}`
                }}
              >
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${currentColors.primary}20` }}
                  >
                    {practitioner.avatar_url ? (
                      <img 
                        src={practitioner.avatar_url}
                        alt={practitioner.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <span className="font-cinzel text-lg" style={{ color: currentColors.primary }}>
                        {practitioner.name?.charAt(0)?.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate" style={{ color: currentColors.text }}>
                      {practitioner.name}
                    </p>
                    <p className="text-xs truncate" style={{ color: currentColors.textMuted }}>
                      {practitioner.email}
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span 
                    className="text-xs px-2 py-0.5 rounded-full"
                    style={{ 
                      backgroundColor: practitioner.status === 'active' ? 'rgba(34, 197, 94, 0.2)' :
                                       practitioner.status === 'pending' ? 'rgba(234, 179, 8, 0.2)' :
                                       'rgba(239, 68, 68, 0.2)',
                      color: practitioner.status === 'active' ? '#22c55e' :
                             practitioner.status === 'pending' ? '#eab308' :
                             '#ef4444'
                    }}
                  >
                    {practitioner.status}
                  </span>
                  {practitioner.bio && (
                    <span 
                      className="text-xs truncate max-w-[100px]"
                      style={{ color: currentColors.textMuted }}
                    >
                      {practitioner.bio}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {practitioners.length === 0 && (
            <div className="text-center py-6">
              <Users className="w-10 h-10 mx-auto mb-2" style={{ color: currentColors.textMuted }} />
              <p style={{ color: currentColors.textMuted }}>No practitioners yet</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
